// src/pages/login/LoginPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '../../App';
import { ArrowUpRight } from 'lucide-react';
import TaskBar from '../../components/taskbar/TaskBar';
import Footer from '../../components/footer/Footer';
import styles from './LoginPage.module.css';

import userService from '../../services/userServices';
import { setAppInitialized } from '../../services/requestServices';
import { showSuccessBanner, showErrorBanner } from '../../components/common/ErrorBannerManager';
import { signInWithGoogle, signInWithGithub, signInWithMicrosoft, signInWithApple, signInWithEmail, signUpWithEmail } from '../../services/firebaseAuth';

export default function LoginPage() {
  const navigate = useNavigate();

  // Our app-level auth context (just a boolean to hide/show pages)
  const { isAuthenticated, setIsAuthenticated } = useAuthContext();

  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    nickname: '',
    password: '',
    confirmPassword: '',
    remember: false
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Authentication error state
  const [authError, setAuthError] = useState(null);

  // If already logged in, skip to "/landing"
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/landing', { replace: true });
    }
    // Mark app as initialized when the login page loads
    // This ensures we don't show 401 errors during initial authentication checks
    setAppInitialized();
  }, [isAuthenticated, navigate]);

  const handleSwitchMode = () => {
    setMode((prev) => (prev === 'login' ? 'register' : 'login'));
    // Reset form data
    setFormData({
      email: '',
      nickname: '',
      password: '',
      confirmPassword: '',
      remember: false
    });
    setErrors({});
    setTouched({});
    // Reset password visibility
    setShowPassword(false);
    setShowConfirmPassword(false);
    // Clear authentication error
    setAuthError(null);
  };

  // Handle Google OAuth2 sign-in
  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setAuthError(null);
    
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        setAuthError(null);
        showSuccessBanner({
          message: 'You have been logged in successfully with Google.',
          title: 'Welcome back!',
          position: 'top-right',
        });
        
        // CRITICAL: Update auth state AND localStorage before navigating
        setIsAuthenticated(true);
        localStorage.setItem('wasAuthed', 'true');
        
        // Use setTimeout to ensure state update propagates before navigation
        setTimeout(() => {
          // Redirect to onboarding billing if first-time user, otherwise landing
          if (result.isFirstTime) {
            navigate('/onboarding/billing', { replace: true });
          } else {
            navigate('/landing', { replace: true });
          }
        }, 100);
      } else {
        setAuthError(result.error || 'Google sign-in failed');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      const errorMessage = error.message || 'An unexpected error occurred during Google sign-in';
      setAuthError(errorMessage);
      showErrorBanner({
        message: errorMessage,
        title: 'Google Sign-in Failed',
        position: 'top-right',
        autoHideDelay: 6000,
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  // Handle GitHub OAuth2 sign-in
  const handleGithubSignIn = async () => {
    setGithubLoading(true);
    setAuthError(null);
    
    try {
      const result = await signInWithGithub();
      if (result.success) {
        setAuthError(null);
        showSuccessBanner({
          message: 'You have been logged in successfully with GitHub.',
          title: 'Welcome back!',
          position: 'top-right',
        });
        
        // CRITICAL: Update auth state AND localStorage before navigating
        setIsAuthenticated(true);
        localStorage.setItem('wasAuthed', 'true');
        
        // Use setTimeout to ensure state update propagates before navigation
        setTimeout(() => {
          // Redirect to onboarding billing if first-time user, otherwise landing
          if (result.isFirstTime) {
            navigate('/onboarding/billing', { replace: true });
          } else {
            navigate('/landing', { replace: true });
          }
        }, 100);
      } else {
        setAuthError(result.error || 'GitHub sign-in failed');
      }
    } catch (error) {
      console.error('GitHub sign-in error:', error);
      setAuthError('An unexpected error occurred during GitHub sign-in');
    } finally {
      setGithubLoading(false);
    }
  };

  // Handle Microsoft OAuth2 sign-in
  const handleMicrosoftSignIn = async () => {
    setMicrosoftLoading(true);
    setAuthError(null);
    
    try {
      const result = await signInWithMicrosoft();
      if (result.success) {
        setAuthError(null);
        showSuccessBanner({
          message: 'You have been logged in successfully with Microsoft.',
          title: 'Welcome back!',
          position: 'top-right',
        });
        
        // CRITICAL: Update auth state AND localStorage before navigating
        setIsAuthenticated(true);
        localStorage.setItem('wasAuthed', 'true');
        
        // Use setTimeout to ensure state update propagates before navigation
        setTimeout(() => {
          // Redirect to onboarding billing if first-time user, otherwise landing
          if (result.isFirstTime) {
            navigate('/onboarding/billing', { replace: true });
          } else {
            navigate('/landing', { replace: true });
          }
        }, 100);
      } else {
        setAuthError(result.error || 'Microsoft sign-in failed');
      }
    } catch (error) {
      console.error('Microsoft sign-in error:', error);
      setAuthError('An unexpected error occurred during Microsoft sign-in');
    } finally {
      setMicrosoftLoading(false);
    }
  };

  // Handle Apple OAuth2 sign-in
  const handleAppleSignIn = async () => {
    setAppleLoading(true);
    setAuthError(null);
    
    try {
      const result = await signInWithApple();
      if (result.success) {
        setAuthError(null);
        showSuccessBanner({
          message: 'You have been logged in successfully with Apple.',
          title: 'Welcome back!',
          position: 'top-right',
        });
        
        // CRITICAL: Update auth state AND localStorage before navigating
        setIsAuthenticated(true);
        localStorage.setItem('wasAuthed', 'true');
        
        // Use setTimeout to ensure state update propagates before navigation
        setTimeout(() => {
          // Redirect to onboarding billing if first-time user, otherwise landing
          if (result.isFirstTime) {
            navigate('/onboarding/billing', { replace: true });
          } else {
            navigate('/landing', { replace: true });
          }
        }, 100);
      } else {
        setAuthError(result.error || 'Apple sign-in failed');
      }
    } catch (error) {
      console.error('Apple sign-in error:', error);
      setAuthError('An unexpected error occurred during Apple sign-in');
    } finally {
      setAppleLoading(false);
    }
  };

  // Enhanced email validation
  const validateEmail = (value) => {
    if (!value) {
      return 'Please enter your email address';
    }
    
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    
    if (value.length > 320) { // RFC 5321 limit
      return 'Email address is too long';
    }
    
    return null;
  };

  // Enhanced password validation for registration
  const validatePassword = (value) => {
    if (!value) {
      return 'Please enter your password';
    }
    
    if (mode === 'register') {
      // Strong password requirements for registration
      if (value.length < 8) {
        return 'Password must be at least 8 characters long';
      }
      
      if (value.length > 128) {
        return 'Password must be less than 128 characters';
      }
      
      if (!/(?=.*[a-z])/.test(value)) {
        return 'Password must contain at least one lowercase letter';
      }
      
      if (!/(?=.*[A-Z])/.test(value)) {
        return 'Password must contain at least one uppercase letter';
      }
      
      if (!/(?=.*\d)/.test(value)) {
        return 'Password must contain at least one number';
      }
      
      if (!/(?=.*[@$!%*?&])/.test(value)) {
        return 'Password must contain at least one special character (@$!%*?&)';
      }
      
      // Check for common weak passwords
      const commonPasswords = ['password', '123456', 'qwerty', 'abc123', 'password123'];
      if (commonPasswords.some(common => value.toLowerCase().includes(common))) {
        return 'Password is too common, please choose a stronger password';
      }
    } else {
      // Login mode - basic validation only
      if (value.length < 1) {
        return 'Please enter your password';
      }
    }
    
    return null;
  };

  // Validate nickname for registration
  const validateNickname = (value) => {
    if (!value) {
      return 'Please enter your nickname';
    }
    
    if (value.length < 2) {
      return 'Nickname must be at least 2 characters long';
    }
    
    if (value.length > 50) {
      return 'Nickname must be less than 50 characters';
    }
    
    // Only allow letters, numbers, spaces, hyphens, and underscores
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(value)) {
      return 'Nickname can only contain letters, numbers, spaces, hyphens, and underscores';
    }
    
    // Don't allow only spaces
    if (value.trim().length === 0) {
      return 'Nickname cannot be only spaces';
    }
    
    return null;
  };

  // Password confirmation validation
  const validatePasswordConfirm = (value) => {
    if (!value) {
      return 'Please confirm your password';
    }
    
    if (formData.password && value !== formData.password) {
      return 'Passwords do not match';
    }
    
    return null;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear authentication error when user starts typing in email or password
    if (authError && (field === 'email' || field === 'password')) {
      setAuthError(null);
    }
    
    // Real-time validation
    if (touched[field]) {
      let error = null;
      switch (field) {
        case 'email':
          error = validateEmail(value);
          break;
        case 'nickname':
          error = validateNickname(value);
          break;
        case 'password':
          error = validatePassword(value);
          // Also revalidate confirm password if it exists
          if (mode === 'register' && formData.confirmPassword && touched.confirmPassword) {
            const confirmError = validatePasswordConfirm(formData.confirmPassword);
            setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
          }
          break;
        case 'confirmPassword':
          error = validatePasswordConfirm(value);
          break;
        default:
          break;
      }
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  // Handle input blur
  const handleInputBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    let error = null;
    const value = formData[field];
    
    switch (field) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'nickname':
        error = validateNickname(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'confirmPassword':
        error = validatePasswordConfirm(value);
        break;
      default:
        break;
    }
    
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    
    newErrors.email = validateEmail(formData.email);
    newErrors.password = validatePassword(formData.password);
    
    if (mode === 'register') {
      newErrors.nickname = validateNickname(formData.nickname);
      newErrors.confirmPassword = validatePasswordConfirm(formData.confirmPassword);
    }
    
    // Remove null errors
    Object.keys(newErrors).forEach(key => {
      if (!newErrors[key]) delete newErrors[key];
    });
    
    setErrors(newErrors);
    setTouched({ email: true, password: true, nickname: true, confirmPassword: true });
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }
    
    setLoading(true);
    try {
      let result;
      if (mode === 'login') {
        console.log('[LoginPage] Attempting Firebase Email/Password login...');
        result = await signInWithEmail(formData.email, formData.password);
        console.log('[LoginPage] Login result:', result);
      } else {
        console.log('[LoginPage] Attempting Firebase Email/Password registration...');
        result = await signUpWithEmail(formData.email, formData.password, formData.nickname);
        console.log('[LoginPage] Registration result:', result);
      }

      if (result.success) {
        setAuthError(null);
        showSuccessBanner({
          message: mode === 'login'
            ? 'You have been logged in successfully.'
            : 'Your account has been created. You are now logged in.',
          title: mode === 'login' ? 'Welcome back!' : 'Account created successfully!',
          position: 'top-right',
        });
        
        // CRITICAL: Update auth state AND localStorage before navigating
        // This ensures the app knows the user is authenticated
        setIsAuthenticated(true);
        localStorage.setItem('wasAuthed', 'true');
        
        // Use setTimeout to ensure state update propagates before navigation
        // This gives React time to process the setState before we navigate
        setTimeout(() => {
          // Redirect to onboarding billing if first-time user, otherwise landing
          if (result.isFirstTime) {
            navigate('/onboarding/billing', { replace: true });
          } else {
            navigate('/landing', { replace: true });
          }
        }, 100); // Small delay to let state update
      } else {
        // Firebase returned an error
        const errorDescription = result.error || (mode === 'login' 
          ? 'Login failed. Please try again.' 
          : 'Registration failed. Please try again.');
        setAuthError(errorDescription);
      }
    } catch (err) {
      console.error('[LoginPage] Error during login/register:', err);
      
      // Enhanced error handling with more specific error messages
      let errorMessage = 'Connection Error';
      let errorDescription = 'Unable to connect to the server. Please check your internet connection and try again.';
      
      if (err.code) {
        // Firebase error
        errorMessage = mode === 'login' ? 'Login Failed' : 'Registration Failed';
        errorDescription = err.message || 'An unexpected error occurred. Please try again.';
      } else if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        if (status === 401) {
          errorMessage = mode === 'login' ? 'Login Failed' : 'Registration Failed';
          errorDescription = mode === 'login' 
            ? 'Invalid email or password. Please check your credentials and try again.'
            : 'Registration failed due to invalid credentials.';
        } else if (status === 404) {
          errorMessage = 'Account Not Found';
          errorDescription = 'No account found with this email address. Please check your email or sign up for a new account.';
        } else if (status === 409) {
          errorMessage = 'Account Already Exists';
          errorDescription = 'An account with this email already exists. Please try logging in or use a different email address.';
        } else if (status === 429) {
          errorMessage = 'Too Many Attempts';
          errorDescription = 'Too many login attempts. Please wait a few minutes before trying again.';
        } else if (status >= 500) {
          errorMessage = 'Server Error';
          errorDescription = 'Our servers are experiencing issues. Please try again in a few minutes.';
        }
      } else if (err.request) {
        // Network error
        errorMessage = 'Network Error';
        errorDescription = 'Unable to reach the server. Please check your internet connection and try again.';
      } else if (err.message && err.message.includes('timeout')) {
        errorMessage = 'Request Timeout';
        errorDescription = 'The request took too long to complete. Please try again.';
      }
      
      setAuthError(errorDescription);
      showErrorBanner({
        message: errorDescription,
        title: errorMessage,
        position: 'top-right',
        autoHideDelay: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Get validation state for styling
  const getValidationState = (field) => {
    if (!touched[field]) return '';
    if (errors[field]) return 'error';
    if (formData[field]) return 'success';
    return '';
  };

  // Get validation state for styling with auth error consideration
  const getValidationStateWithAuth = (field) => {
    // If there's an auth error and this is email or password field, show error state
    if (authError && (field === 'email' || field === 'password')) {
      return 'error';
    }
    return getValidationState(field);
  };

  return (
    <>
      <TaskBar />
      <div className={styles.loginPage}>
        {/* Main Content */}
      <main className={styles.main}>
        <div className={styles.loginContainer}>
          <div className={styles.loginBox}>
            <div className={styles.loginHeader}>
              <h1 className={styles.loginTitle}>
                {mode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h1>
              <p className={styles.loginSubtitle}>
                {mode === 'login' 
                  ? 'Sign in to your Foundry account' 
                  : 'Join Foundry and start building'
                }
              </p>
            </div>

            {/* Authentication Error Banner */}
            {authError && (
              <div className={styles.authErrorBanner}>
                <div className={styles.authErrorContent}>
                  <svg className={styles.authErrorIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                  <span className={styles.authErrorText}>{authError}</span>
                </div>
              </div>
            )}

            {/* OAuth Section */}
            <div className={styles.oauthSection}>
              <button
                type="button"
                className={styles.appleSignInButton}
                onClick={handleAppleSignIn}
                disabled={appleLoading}
              >
                {appleLoading ? (
                  <span className={styles.loadingText}>
                    <span className={styles.spinner}></span>
                    Continuing with Apple...
                  </span>
                ) : (
                  <>
                    <svg className={styles.appleIcon} width="20" height="20" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    Continue with Apple
                  </>
                )}
              </button>

              <button
                type="button"
                className={styles.githubSignInButton}
                onClick={handleGithubSignIn}
                disabled={githubLoading}
              >
                {githubLoading ? (
                  <span className={styles.loadingText}>
                    <span className={styles.spinner}></span>
                    Continuing with GitHub...
                  </span>
                ) : (
                  <>
                    <svg className={styles.githubIcon} width="20" height="20" viewBox="0 0 24 24">
                      <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.13 6.839 9.46.5.09.68-.22.68-.48V19.5c-2.67.5-3.28-1.14-3.28-1.14-.45-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6 1.02.07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.65.71 1.02 1.6 1.02 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .26.18.58.68.48C19.135 20.13 22 16.42 22 12c0-5.523-4.477-10-10-10z"/>
                    </svg>
                    Continue with GitHub
                  </>
                )}
              </button>

              <button
                type="button"
                className={styles.googleSignInButton}
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
              >
                {googleLoading ? (
                  <span className={styles.loadingText}>
                    <span className={styles.spinner}></span>
                    Continuing with Google...
                  </span>
                ) : (
                  <>
                    <svg className={styles.googleIcon} width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>

              <button
                type="button"
                className={styles.microsoftSignInButton}
                onClick={handleMicrosoftSignIn}
                disabled={microsoftLoading}
              >
                {microsoftLoading ? (
                  <span className={styles.loadingText}>
                    <span className={styles.spinner}></span>
                    Continuing with Microsoft...
                  </span>
                ) : (
                  <>
                    <svg className={styles.microsoftIcon} width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M0 0h11v11H0z"/>
                      <path fill="#7FBA00" d="M13 0h11v11H13z"/>
                      <path fill="#00A4EF" d="M0 13h11v11H0z"/>
                      <path fill="#FFB900" d="M13 13h11v11H13z"/>
                    </svg>
                    Continue with Microsoft
                  </>
                )}
              </button>
            </div>

            <div className={styles.divider}>
              <span className={styles.dividerText}>or</span>
            </div>

            <form onSubmit={handleSubmit} className={styles.loginForm}>
              <div className={`${styles.formField} ${styles[getValidationStateWithAuth('email')]}`}>
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onBlur={() => handleInputBlur('email')}
                  placeholder="Enter your email address"
                  autoComplete="email"
                  maxLength={320}
                  disabled={loading}
                />
                {errors.email && touched.email && (
                  <span className={styles.errorMessage}>{errors.email}</span>
                )}
              </div>

              {mode === 'register' && (
                <div className={`${styles.formField} ${styles[getValidationStateWithAuth('nickname')]}`}>
                  <label htmlFor="nickname">Display Name</label>
                  <input
                    type="text"
                    id="nickname"
                    name="nickname"
                    value={formData.nickname}
                    onChange={(e) => handleInputChange('nickname', e.target.value)}
                    onBlur={() => handleInputBlur('nickname')}
                    placeholder="Choose a display name"
                    autoComplete="username"
                    maxLength={50}
                    disabled={loading}
                  />
                  {errors.nickname && touched.nickname && (
                    <span className={styles.errorMessage}>{errors.nickname}</span>
                  )}
                </div>
              )}

              <div className={`${styles.formField} ${styles[getValidationStateWithAuth('password')]}`}>
                <label htmlFor="password">Password</label>
                <div className={styles.passwordWrapper}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    onBlur={() => handleInputBlur('password')}
                    placeholder={mode === 'login' ? 'Enter your password' : 'Create a strong password'}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    maxLength={128}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && touched.password && (
                  <span className={styles.errorMessage}>{errors.password}</span>
                )}
              </div>

              {mode === 'register' && (
                <div className={`${styles.formField} ${styles[getValidationStateWithAuth('confirmPassword')]}`}>
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      onBlur={() => handleInputBlur('confirmPassword')}
                      placeholder="Confirm your password"
                      autoComplete="new-password"
                      maxLength={128}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && touched.confirmPassword && (
                    <span className={styles.errorMessage}>{errors.confirmPassword}</span>
                  )}
                </div>
              )}

              {mode === 'login' && (
                <div className={styles.checkboxField}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.remember}
                      onChange={(e) => handleInputChange('remember', e.target.checked)}
                      disabled={loading}
                    />
                    <span className={styles.checkboxText}>Keep me signed in</span>
                  </label>
                </div>
              )}

              <div className={styles.modeSwitch}>
                {mode === 'login' ? (
                  <span>
                    Don't have an account?{' '}
                    <button type="button" className={styles.linkButton} onClick={handleSwitchMode}>
                      Create account
                    </button>
                  </span>
                ) : (
                  <span>
                    Already have an account?{' '}
                    <button type="button" className={styles.linkButton} onClick={handleSwitchMode}>
                      Sign in
                    </button>
                  </span>
                )}
              </div>

              <button
                type="submit"
                className={`${styles.submitButton} ${loading ? styles.loading : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <span className={styles.loadingText}>
                    <span className={styles.spinner}></span>
                    {mode === 'login' ? 'Signing In...' : 'Creating Account...'}
                  </span>
                ) : (
                  mode === 'login' ? 'Sign In' : 'Create Account'
                )}
              </button>
            </form>
          </div>
        </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
